'use client';
import React, { useCallback } from 'react';
import styled from '@benzinga/themetron';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { AutocompleteManager } from '@benzinga/autocomplete-manager';
import { Ticker } from '@benzinga/session';
import { QuotesManager, Logo } from '@benzinga/quotes-manager';

export interface BZLogoSearchProps {
  attrs: {
    data: {
      title: string;
      subtitle: string;
      bz_default_logo: string;
      bz_default_logo_ticker: string;
    };
  };
}
const SEARCH_ITEM_LOGO_SIZE = 100;

const IMAGE_REQUEST_PARAMS = {
  fields: 'mark_vector_light,logo_vector_light,mark_composite_light,mark_composite_dark',
  scale: `${SEARCH_ITEM_LOGO_SIZE}x${SEARCH_ITEM_LOGO_SIZE}`,
  search_keys_type: 'symbol',
};

export const BZLogoSearch: React.FC<BZLogoSearchProps> = ({ attrs }) => {
  const [securitiesList, setSecuritiesList] = React.useState<Ticker[]>([]);
  const [selectedTickerLogo, setSelectedTickerLogo] = React.useState('');
  const [selectedTickerName, setSelectedTickerName] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [isSearching, setIsSearching] = React.useState<boolean>(false);

  const [selectedTicker, setSelectedTicker] = React.useState<Ticker | null>(null);

  const [selectedLogoType, setSelectedLogoType] = React.useState<string>('mark_composite_dark');
  const [mode, setMode] = React.useState<string>('dark');

  const [searchedLogo, setSearchedLogo] = React.useState<Record<string, Logo>>();
  const defaultTicker = attrs?.data?.bz_default_logo_ticker ?? 'TSLA';

  const session = React.useContext(SessionContext);
  const autocompleteManager = session.getManager(AutocompleteManager);
  const quotesManager = session.getManager(QuotesManager);
  const updateTickersLogos = useCallback(
    async (incomeTickers: Ticker[], symbolsList?: string[]) => {
      try {
        const tickers = incomeTickers ? [...incomeTickers] : [];

        const { ok: logos } = await quotesManager.getQuotesLogosMapped(
          symbolsList ?? tickers.map(item => item.symbol),
          IMAGE_REQUEST_PARAMS,
        );

        if (logos) {
          setSearchedLogo(logos);
          setSecuritiesList(injectLogoInTickersByField(tickers, logos, 'mark_vector_light'));
        }
      } catch (e) {
        console.log('LOGOS ERROR', e);
      }
    },
    [quotesManager],
  );

  const injectLogoInTickersByField = (
    tickers: Ticker[],
    logos: Record<string, Logo>,
    selectedLogoType = 'mark_composite_dark',
  ): Ticker[] => {
    if (!logos) {
      return tickers;
    }

    tickers.forEach(ticker => {
      const logo = logos[ticker.symbol];
      if (logo) {
        ticker.logo =
          logo?.files?.[selectedLogoType] ??
          logo?.files?.['logo_vector_light'] ??
          logo?.files?.['mark_composite_light'] ??
          null;
      }
    });

    return tickers;
  };

  React.useEffect(() => {
    const getInitialSelectedTicker = async () => {
      const res = await autocompleteManager.getAutocompleteListing(defaultTicker);
      const tickers: Ticker[] = res?.ok?.result.slice(0, 1) ?? [];

      const { ok: logos } = await quotesManager.getQuotesLogosMapped([defaultTicker], IMAGE_REQUEST_PARAMS);

      if (logos) {
        setSearchedLogo(logos);
        const initSelected = injectLogoInTickersByField(tickers, logos, 'mark_composite_dark');

        if (initSelected.length > 0) {
          setSelectedTicker(initSelected[0]);
          setSelectedTickerName(initSelected[0]?.logo ?? '');
          setSelectedTickerLogo(initSelected[0]?.logo ?? '');
        }
      }
    };

    getInitialSelectedTicker();
  }, [autocompleteManager, defaultTicker, quotesManager]);

  const searchSymbol = useCallback(async () => {
    setIsSearching(true);
    if (search.length < 1) {
      setSecuritiesList([]);
    } else {
      const res = await autocompleteManager.getAutocompleteListing(search);
      const tickers: Ticker[] = res?.ok?.result.slice(0, 10) ?? [];
      const tickersList = tickers.map(item => item.symbol);
      await updateTickersLogos(tickers, tickersList);
    }
    setIsSearching(false);
  }, [search, autocompleteManager, updateTickersLogos]);

  const handleChange = (currentselectedticker, logoType: string, mode = 'light') => {
    if (searchedLogo) {
      setSelectedLogoType(logoType);

      const data = injectLogoInTickersByField([currentselectedticker], searchedLogo, logoType);
      setSelectedTicker(currentselectedticker);

      if (data.length > 0) {
        const currentTicker = data[0];
        setSelectedTickerName(currentTicker?.name ?? '');
        setSelectedTickerLogo(currentTicker?.logo ?? '');
        setMode(mode);
      }
    }
  };

  const handleFirstSelection = () => {
    if (securitiesList.length > 0) {
      handleChange(securitiesList[0], selectedLogoType);
      setSearch('');
      setSecuritiesList([]);
    }
  };

  return (
    <BZLogoSearchWrap className="my-10">
      <div className="search-layout">
        <div className="search-column">
          <h2>{attrs.data.title}</h2>
          <p>{attrs.data.subtitle}</p>
          <div className="w-full searchbar">
            <Icon className="searchicon" icon={faSearch} strokeWidth={2} />
            <input
              className="w-full inner-search"
              onChange={e => setSearch(e.target.value)}
              onKeyPress={event => {
                if (event.key === 'Enter') {
                  handleFirstSelection();
                }
              }}
              onKeyUp={searchSymbol}
              placeholder="Enter a company name ..."
              type="text"
              value={search}
            />
            {securitiesList.length > 0 && (
              <div className="search-box max-h-96 overflow-auto bg-white z-10">
                {securitiesList.map((ticker, index) => {
                  const { logo, name, symbol } = ticker;
                  return (
                    <a
                      className="flex px-4 py-2 gap-4 hover:bg-bzblue-100 align-middle text-bzblue-900"
                      key={index}
                      onClick={e => {
                        e.preventDefault();
                        handleChange(ticker, selectedLogoType);
                        setSearch('');
                        setSecuritiesList([]);
                      }}
                    >
                      <img alt={`${name} logo`} className="w-6 h-6" src={logo} />
                      <div className="search-symbol">{symbol}</div>
                      <div className="flex-1 flex-grow search-ticker">{name}</div>
                    </a>
                  );
                })}
              </div>
            )}
            {securitiesList.length === 0 && search.length > 1 && !isSearching && (
              <div className="search-box max-h-96 overflow-auto bg-white">
                <div className="px-4 py-2 text-bzblue-900">No results found</div>
              </div>
            )}
          </div>
        </div>
        <div className="preview-column">
          <div className={`logo-preview-card ${mode === 'dark' ? 'is-dark' : 'is-light'}`}>
            <div className="logo-preview-meta">
              <span className="logo-preview-label">Preview</span>
              {selectedTicker?.symbol && <span className="logo-preview-symbol">{selectedTicker.symbol}</span>}
            </div>
            <div className="selected-logo">
              {selectedTickerLogo ? (
                <img alt={`${selectedTickerName} logo`} key={selectedTickerLogo} src={selectedTickerLogo} />
              ) : (
                <span className="logo-preview-empty">Select a company to preview</span>
              )}
            </div>
          </div>
          <div aria-label="Logo style" className="toggle-logo-design" role="radiogroup">
            {[
              { id: 'mark_composite_dark', label: 'Mark Composite Dark', mode: 'dark' },
              { id: 'mark_composite_light', label: 'Mark Composite Light', mode: 'light' },
              { id: 'logo_vector_light', label: 'Logo Vector Light', mode: 'light' },
              { id: 'mark_vector_light', label: 'Mark Vector Light', mode: 'light' },
            ].map(option => (
              <label
                className={selectedLogoType === option.id ? 'is-active' : undefined}
                htmlFor={option.id}
                key={option.id}
              >
                <input
                  checked={selectedLogoType === option.id}
                  id={option.id}
                  name="logo_preview_type"
                  onChange={() => handleChange(selectedTicker, option.id, option.mode)}
                  type="radio"
                  value={option.id}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </BZLogoSearchWrap>
  );
};

export const BZLogoSearchWrap = styled.div`
  .search-layout {
    display: flex;
    align-items: center;
    gap: 24px;

    @media screen and (max-width: 799px) {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }
  }

  .search-column {
    flex: 1.15;
    min-width: 0;

    h2 {
      color: #192940;
      font-size: 36px;
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: -0.02em;
      text-transform: uppercase;
      margin: 0 0 10px;

      @media screen and (max-width: 576px) {
        font-size: 28px;
      }
    }

    p {
      color: #3d4f66;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.5;
      margin: 0 0 16px;
      max-width: 34em;
    }
  }

  .preview-column {
    flex: 0.85;
    min-width: 0;
    max-width: 360px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    @media screen and (max-width: 799px) {
      max-width: none;
    }
  }

  .logo-preview-card {
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(25, 41, 64, 0.1);
    box-shadow: 0 10px 28px rgba(25, 41, 64, 0.08);
    transition:
      border-color 180ms ease,
      box-shadow 180ms ease;

    &.is-dark {
      border-color: rgba(25, 41, 64, 0.35);
      box-shadow: 0 14px 36px rgba(25, 41, 64, 0.22);

      .logo-preview-meta {
        background: #121f33;
        border-bottom-color: rgba(255, 255, 255, 0.08);

        .logo-preview-label {
          color: rgba(255, 255, 255, 0.55);
        }

        .logo-preview-symbol {
          color: #ffffff;
        }
      }

      .selected-logo {
        background: radial-gradient(120% 90% at 50% 35%, #243554 0%, #192940 55%, #121f33 100%);
      }

      .logo-preview-empty {
        color: rgba(255, 255, 255, 0.45);
      }

      img {
        outline-color: oklch(1 0 0 / 0.1);
      }
    }

    &.is-light {
      .selected-logo {
        background-color: #f4f7fb;
        background-image:
          linear-gradient(45deg, rgba(25, 41, 64, 0.04) 25%, transparent 25%),
          linear-gradient(-45deg, rgba(25, 41, 64, 0.04) 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, rgba(25, 41, 64, 0.04) 75%),
          linear-gradient(-45deg, transparent 75%, rgba(25, 41, 64, 0.04) 75%);
        background-size: 16px 16px;
        background-position:
          0 0,
          0 8px,
          8px -8px,
          -8px 0;
      }

      img {
        outline-color: oklch(0 0 0 / 0.1);
      }
    }
  }

  .logo-preview-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    background: #ffffff;
    border-bottom: 1px solid rgba(25, 41, 64, 0.08);
  }

  .logo-preview-label {
    color: #5b7292;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .logo-preview-symbol {
    color: #192940;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
  }

  .searchbar {
    border-radius: 10px;
    background: rgba(63, 131, 248, 0.1);
    border: 1px solid transparent;
    position: relative;
    display: flex;
    align-items: center;
    padding: 12px 14px;

    &:focus-within {
      border-color: rgba(63, 131, 248, 0.35);
      background: rgba(63, 131, 248, 0.12);
    }

    .searchicon {
      display: inline-flex;
      width: 20px;
      flex-shrink: 0;
      margin-right: 10px;
      svg {
        color: #5b7292;
      }
    }

    .inner-search {
      background: transparent;
      width: 100%;
      color: #192940;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      outline: none;

      &::placeholder {
        color: #5b7292;
      }
    }

    .search-box {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      width: 100%;
      display: block;
      max-height: 240px;
      border-radius: 10px;
      background: #ffffff;
      border: 1px solid rgba(25, 41, 64, 0.1);
      box-shadow: 0 12px 32px rgba(32, 50, 71, 0.12);
      overflow: hidden;

      a {
        cursor: pointer;
        color: #192940;

        .search-symbol {
          font-weight: 600;
          min-width: 60px;
        }

        .search-ticker {
          color: #5b7292;
        }
      }
    }
  }

  .selected-logo {
    width: 100%;
    min-height: 140px;
    height: 140px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    transition: background-color 180ms ease;

    img {
      max-width: 100%;
      max-height: 96px;
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 10px;
      outline: 1px solid transparent;
      outline-offset: -1px;
      filter: drop-shadow(0 8px 18px rgba(18, 31, 51, 0.18));
      animation: logo-preview-in 220ms cubic-bezier(0.2, 0, 0, 1);
    }
  }

  .logo-preview-empty {
    color: #5b7292;
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    padding: 0 16px;
  }

  @keyframes logo-preview-in {
    from {
      opacity: 0;
      transform: scale(0.96);
      filter: blur(4px) drop-shadow(0 8px 18px rgba(18, 31, 51, 0.18));
    }
    to {
      opacity: 1;
      transform: scale(1);
      filter: blur(0) drop-shadow(0 8px 18px rgba(18, 31, 51, 0.18));
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .selected-logo img {
      animation: none;
    }

    .toggle-logo-design label {
      transition: none;

      &:active {
        transform: none;
      }
    }
  }

  .toggle-logo-design {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid rgba(25, 41, 64, 0.1);
      background: #ffffff;
      color: #3d4f66;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.3;
      cursor: pointer;
      text-align: left;
      transition:
        border-color 140ms ease,
        background-color 140ms ease,
        color 140ms ease,
        box-shadow 140ms ease,
        transform 100ms ease;

      &:hover {
        border-color: rgba(63, 131, 248, 0.35);
        background: rgba(63, 131, 248, 0.04);
      }

      &:active {
        transform: scale(0.96);
      }

      &.is-active {
        border-color: #3f83f8;
        background: rgba(63, 131, 248, 0.1);
        color: #192940;
        box-shadow: 0 0 0 3px rgba(63, 131, 248, 0.12);
      }

      input {
        accent-color: #3f83f8;
        margin: 0;
        flex-shrink: 0;
      }

      span {
        min-width: 0;
      }
    }
  }
`;
